import api from '~/utils/api';
import type { AxiosRequestConfig } from 'axios';
import { checkoutPath, checkoutDestination, legacyAccountBillingConfig, isStripeHostedUrl, billingResponseData } from '~/utils/billing-route.mjs';

export interface BillingConfig {
  provider: 'fastspring' | 'stripe' | 'ios' | 'android';
  stripe_enabled: boolean;
  stripe_products: string[];
  checkout_available?: boolean;
  has_subscription: boolean;
  has_paid_access?: boolean;
  current_product_id?: string;
  subscription_status?: string;
  auto_renew_disabled?: boolean;
  intro_eligible?: boolean;
  points_balance?: number;
  subscription_provider: string;
  sub_ended: number;
  stripe_portal_available: boolean;
  stripe_cancellation_available?: boolean;
  stripe_upgrade_products?: string[];
  legacy_subscription_present?: boolean;
  legacy_verification_required?: boolean;
}

export const billingService = {
  async config(options?: AxiosRequestConfig): Promise<BillingConfig> {
    try {
      const response = await api.post<{ data: BillingConfig }>('music/v1/billing/config', {}, options);
      return billingResponseData(response);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Read fresh status; missing payment configuration permits viewing only.
        const account = billingResponseData(await api.post('music/v1/status', {}, options));
        let catalog: Array<{ product_id: string; group_id?: string }> = [];
        try { catalog = billingResponseData(await api.post('music/v1/products', {}, options)).list || []; }
        catch { /* Show access without guessing its provider if the catalog fails. */ }
        return legacyAccountBillingConfig(account, Array.isArray(catalog) ? catalog : []) as BillingConfig;
      }
      throw error;
    }
  },
  async checkoutPath(productId: string): Promise<string> {
    return checkoutPath(await this.config(), productId);
  },
  async checkoutDestination(productId: string): Promise<string> {
    return checkoutDestination(await this.config(), productId, (id: string) => this.checkout(id));
  },
  async checkout(productId: string): Promise<string> {
    const response = await api.post<{ data: { url: string } }>('music/v1/billing/stripe/checkout', { product_id: productId });
    const data = billingResponseData(response);
    if (!isStripeHostedUrl(data.url)) throw new Error('Invalid checkout URL');
    return data.url;
  },
  async status(sessionId: string, options?: AxiosRequestConfig) {
    const response = await api.post<{ data: { status: string; payment_status: string; fulfilled: boolean } }>(
      'music/v1/billing/stripe/status', { session_id: sessionId }, options);
    return billingResponseData(response);
  },
  async portal(): Promise<string> {
    const response = await api.post<{ data: { url: string } }>('music/v1/billing/stripe/portal', {});
    const data = billingResponseData(response);
    if (!isStripeHostedUrl(data.url)) throw new Error('Invalid portal URL');
    return data.url;
  },
  async upgrade(productId: string): Promise<string> {
    const data = billingResponseData(await api.post('music/v1/billing/stripe/upgrade', { product_id: productId }));
    if (!isStripeHostedUrl(data.url)) throw new Error('Invalid upgrade URL');
    return data.url;
  },
  async upgradeStatus(productId: string, options?: AxiosRequestConfig) {
    return billingResponseData(await api.post('music/v1/billing/stripe/upgrade/status', { product_id: productId }, options));
  },
};
